import { useEffect, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const fmt = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

const WHY = {
  more_support: "Your latest answer reflects a more supportive experience than your earlier one.",
  attention_now:
    "Your latest answer reflects less support than before, so it may be worth gentle attention.",
  consistent: "You reported a similar supportive experience in both discoveries.",
};

const Observation = ({ item, kind, oldDate, newDate, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testid} className="border-t border-forest/10 pt-4 first:border-t-0 first:pt-0">
      <p className="text-base font-semibold leading-snug text-forest">{item.title}</p>
      <p className="mt-1.5 text-sm leading-relaxed text-charcoal/75">{item.sentence}</p>
      {item.question_text && (
        <>
          <button
            data-testid={`${testid}-details-toggle`}
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-gold-dark transition-colors hover:text-forest"
          >
            Details
            <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (
            <div data-testid={`${testid}-details`} className="mt-3 rounded-2xl bg-sage/50 p-4 text-sm">
              <p className="italic leading-relaxed text-taupe">"{item.question_text}"</p>
              {kind === "consistent" ? (
                <p className="mt-2.5 text-charcoal/80">
                  <span className="font-semibold text-forest">Both times: </span>
                  {item.from_text}
                </p>
              ) : (
                <>
                  <p className="mt-2.5 text-charcoal/80">
                    <span className="font-semibold text-forest">Previous ({fmt(oldDate)}): </span>
                    {item.from_text}
                  </p>
                  <p className="mt-1.5 text-charcoal/80">
                    <span className="font-semibold text-forest">Most recent ({fmt(newDate)}): </span>
                    {item.to_text}
                  </p>
                </>
              )}
              <p className="mt-2.5 text-xs leading-relaxed text-taupe">{WHY[kind]}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Bucket = ({ title, items, kind, testid, accent, oldDate, newDate }) => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, 3);
  return (
    <div data-testid={testid} className="rounded-[1.75rem] border border-forest/10 bg-white/75 p-6 sm:p-7">
      <h4 className={`font-serif text-lg ${accent}`}>{title}</h4>
      {items.length === 0 ? (
        <p className="mt-3 text-sm text-taupe">Nothing stood out here across your discoveries.</p>
      ) : (
        <div className="mt-4 flex flex-col gap-4">
          {visible.map((it, i) => (
            <Observation
              key={i}
              item={it}
              kind={kind}
              oldDate={oldDate}
              newDate={newDate}
              testid={`${testid}-item-${i}`}
            />
          ))}
        </div>
      )}
      {items.length > 3 && (
        <button
          data-testid={`${testid}-toggle`}
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-forest/20 px-4 py-2 text-xs font-semibold text-forest transition-colors hover:border-gold"
        >
          {expanded ? "Show Less" : `Show More Observations (${items.length - 3} more)`}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`} />
        </button>
      )}
    </div>
  );
};

const buildSummary = (comparison, oldDate, newDate) => {
  const s = comparison.summary;
  const parts = [];
  parts.push(
    `Between ${fmt(oldDate)} and ${fmt(newDate)}, ` +
      (s.more_support.length > 0
        ? `your answers reflect more support in ${s.more_support.length} area${s.more_support.length > 1 ? "s" : ""}, including ${s.more_support[0].title.toLowerCase()}.`
        : "your answers stayed close to your earlier discovery.")
  );
  if (s.consistent.length > 0) {
    parts.push(
      `${s.consistent.length} supportive habit${s.consistent.length > 1 ? "s" : ""} stayed consistent, including ${s.consistent[0].title.toLowerCase()}.`
    );
  }
  if (s.attention_now.length > 0) {
    parts.push(
      `${s.attention_now.length} area${s.attention_now.length > 1 ? "s" : ""}, like ${s.attention_now[0].title.toLowerCase()}, may be worth gentle attention now.`
    );
  } else {
    parts.push("No area showed less support than before.");
  }
  return parts.slice(0, 3).join(" ");
};

export const JourneyGlance = ({ entries }) => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!entries || entries.length < 2) return;
    const older = entries[0].assessment_id;
    const newer = entries[entries.length - 1].assessment_id;
    api
      .get(`/assessments/compare?older=${older}&newer=${newer}`)
      .then(({ data }) => setData(data))
      .catch(() => setFailed(true));
  }, [entries]);

  if (!entries || entries.length < 2 || failed) return null;

  const oldDate = entries[0].created_at;
  const newDate = entries[entries.length - 1].created_at;
  const comparable = data?.comparison?.comparable;

  return (
    <div
      data-testid="journey-glance"
      className="rounded-[2rem] border border-forest/10 bg-white/75 p-7 shadow-[0_12px_30px_rgba(23,63,53,0.07)] sm:p-9"
    >
      <h3 className="font-serif text-xl text-forest">Your Journey at a Glance</h3>
      {!data ? (
        <div className="mt-6 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-forest" />
        </div>
      ) : !comparable ? (
        <p className="mt-4 text-sm leading-relaxed text-charcoal/70">{data.comparison.note}</p>
      ) : (
        <>
          <p data-testid="glance-summary" className="mt-3 max-w-3xl text-base leading-relaxed text-charcoal/80">
            {buildSummary(data.comparison, oldDate, newDate)}
          </p>
          {data.comparison.status_change && (
            <p className="mt-2 text-sm italic leading-relaxed text-taupe">
              {data.comparison.status_change}
            </p>
          )}
          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Bucket
              title="Where You've Reported More Support"
              items={data.comparison.summary.more_support}
              kind="more_support"
              testid="glance-more-support"
              accent="text-gold-dark"
              oldDate={oldDate}
              newDate={newDate}
            />
            <Bucket
              title="What Has Stayed Consistent"
              items={data.comparison.summary.consistent}
              kind="consistent"
              testid="glance-consistent"
              accent="text-forest"
              oldDate={oldDate}
              newDate={newDate}
            />
            <Bucket
              title="What May Need Attention Now"
              items={data.comparison.summary.attention_now}
              kind="attention_now"
              testid="glance-attention-now"
              accent="text-forest"
              oldDate={oldDate}
              newDate={newDate}
            />
          </div>
          <p className="mt-5 text-xs leading-relaxed text-taupe">
            Changes in your answers describe what you reported at each point in time. They do
            not prove medical improvement or deterioration.
          </p>
        </>
      )}
    </div>
  );
};
